import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./attendant-card.module.css";
import ranaWaiting from "illustrations/rana/rana-waiting.webp";
import ranaChilling from "illustrations/rana/rana-chilling.webp";
import useIsMobile from "hooks/detect-mobile-screen";
import { CircleCheck, CircleX, Info } from "lucide-react";

const AttendantCard = ({ participants, currentPlayerId, game }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile(980);

  const confirmedParticipants = participants.filter(
    (participant) => participant.status === "CONFIRMED"
  );

  const cancelledParticipants = participants.filter(
    (participant) => participant.status === "CANCELLED"
  );

  const isParticipating = confirmedParticipants.find(
    (participant) => participant.player_id === currentPlayerId
  );

  const substitutes = Math.max(
    0,
    confirmedParticipants.length - game.soft_limit
  );

  return (
    <div className={styles["attendee-card-container"]}>
      <div className={styles["attendee-card"]}>
        {game.status.toUpperCase() === "SCHEDULED" ? (
          <>
            <div className={`${styles["attendee-header"]} mb-24`}>
              {confirmedParticipants.length > 0 ? (
                <div className={styles.substitutes}>
                  <div
                    className={styles["participants-header"]}
                    data-testid="participants-header"
                  >
                    <h2 className="subtitle-2-variant">
                      {t("dashboard.next-game.participants.title")}
                    </h2>
                    <p className="title-4">
                      {t("dashboard.next-game.participants.description", {
                        count: Math.min(
                          confirmedParticipants.length,
                          game.soft_limit
                        ),
                      })}
                    </p>
                  </div>
                  {substitutes > 0 && (
                    <div
                      className={styles["subtitutes-wrapper"]}
                      data-testid="substitutes"
                    >
                      <p className="title-4">+ {substitutes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <h2 className="title-4">
                  {t("dashboard.next-game.participants.empty")}
                </h2>
              )}
              {confirmedParticipants.length > 0 && (
                <div className={styles["disclaimer-wrapper"]}>
                  {isParticipating ? (
                    <CircleCheck
                      size={14}
                      className="icon icon-success"
                      data-testid="participating-icon"
                    />
                  ) : (
                    <CircleX
                      size={14}
                      className="icon icon-danger"
                      data-testid="not-participating-icon"
                    />
                  )}
                  {!isMobile && (
                    <p className="title-4">
                      {isParticipating
                        ? t("dashboard.next-game.participants.participating")
                        : t(
                            "dashboard.next-game.participants.not-participating"
                          )}
                    </p>
                  )}
                </div>
              )}
            </div>
            {confirmedParticipants.length > 0 ? (
              <ul className={styles["participant-list"]}>
                <div className={styles["participant-grid"]}>
                  {confirmedParticipants.map((participant, index) => (
                    <li key={index} className={styles.participant}>
                      <div className={styles["participant-name"]}>
                        <div className={styles.icon}>
                          <p className="title-4">
                            {participant.name[0]}
                            {participant.name.split(" ")[1][0]}
                          </p>
                        </div>
                        <p className="title-3-variant">{participant.name}</p>
                      </div>
                    </li>
                  ))}
                </div>
              </ul>
            ) : (
              <img
                src={ranaWaiting}
                alt={t("dashboard.next-game.participants.none")}
                className={styles["empty-participants"]}
              />
            )}
          </>
        ) : (
          <>
            <div className={`${styles["participants-header"]} mb-24`}>
              <CircleX
                size={14}
                className="icon icon-danger"
                data-testid="cancelled-icon"
              />
              <h2 className="title-4">
                {t("dashboard.next-game.cancelled.title")}
              </h2>
            </div>
            <img
              src={ranaChilling}
              alt={t("dashboard.next-game.participants.none")}
              className={styles["empty-participants"]}
            />
          </>
        )}
      </div>
      {cancelledParticipants.length > 0 && (
        <div className={styles["informations"]}>
          <Info
            size={16}
            className="icon icon-information"
            data-testid="information-icon"
          />

          <p className="subtitle-2-variant">
            {t(
              `dashboard.next-game.participants.cancelled_${
                cancelledParticipants.length === 1 ? "singular" : "plural"
              }`,
              {
                player: cancelledParticipants[0].name,
                count: cancelledParticipants.length - 1,
              }
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default AttendantCard;
